/**
 * Les états, rendus lisibles.
 *
 * **Jamais la couleur seule.** Chaque état porte un mot en toutes lettres et
 * un signe ; la teinte ne fait que confirmer. C'est ce qui le rend lisible en
 * noir et blanc, à un daltonien, et dans une capture d'écran envoyée par
 * courriel — les trois situations où un point de couleur ne dit plus rien.
 */

type Ton = "succes" | "attente" | "erreur" | "info" | "verrou" | "neutre";

const ETATS_AFFAIRE: Record<string, { mot: string; ton: Ton; signe: string }> = {
  READY: { mot: "Prête", ton: "succes", signe: "●" },
  PROVISIONING: { mot: "En préparation", ton: "attente", signe: "◐" },
  FAILED: { mot: "En échec", ton: "erreur", signe: "▲" },
  ARCHIVED: { mot: "Archivée", ton: "neutre", signe: "◇" },
};

const ETATS_PIECE: Record<string, { mot: string; ton: Ton; signe: string }> = {
  SELECTED: { mot: "Sélectionnée", ton: "info", signe: "○" },
  IMPORTING: { mot: "Import en cours", ton: "attente", signe: "◐" },
  READY: { mot: "Prête", ton: "succes", signe: "●" },
  FAILED: { mot: "En échec", ton: "erreur", signe: "▲" },
  REMOTE_CHANGED: { mot: "Nouvelle version", ton: "attente", signe: "↑" },
  REMOTE_DELETED: { mot: "Supprimée à la source", ton: "erreur", signe: "⊘" },
  REMOVED: { mot: "Retirée", ton: "neutre", signe: "–" },
};

const ETATS_TRAVAIL: Record<string, { mot: string; ton: Ton; signe: string }> = {
  QUEUED: { mot: "En file", ton: "neutre", signe: "◇" },
  DOWNLOADING: { mot: "Téléchargement", ton: "attente", signe: "↓" },
  VALIDATING: { mot: "Vérification", ton: "attente", signe: "◐" },
  SCANNING: { mot: "Analyse", ton: "attente", signe: "◐" },
  STORING: { mot: "Archivage", ton: "attente", signe: "◐" },
  MATERIALIZING: { mot: "Mise en place", ton: "attente", signe: "◐" },
  PROCESSING: { mot: "Traitement", ton: "attente", signe: "◐" },
  READY: { mot: "Terminé", ton: "succes", signe: "●" },
  FAILED: { mot: "En échec", ton: "erreur", signe: "▲" },
  CANCELED: { mot: "Annulé", ton: "neutre", signe: "–" },
};

const ETATS_INSTANTANE: Record<string, { mot: string; ton: Ton; signe: string }> = {
  DRAFT: { mot: "Brouillon", ton: "neutre", signe: "◇" },
  PROCESSING: { mot: "En préparation", ton: "attente", signe: "◐" },
  READY: { mot: "Prêt", ton: "info", signe: "○" },
  ACTIVE: { mot: "Actif", ton: "succes", signe: "●" },
  SUPERSEDED: { mot: "Dépassé", ton: "neutre", signe: "–" },
  FAILED: { mot: "En échec", ton: "erreur", signe: "▲" },
};

const ETATS_MISSION: Record<string, { mot: string; ton: Ton; signe: string }> = {
  QUEUED: { mot: "En file", ton: "neutre", signe: "◇" },
  RUNNING: { mot: "En cours", ton: "attente", signe: "◐" },
  SUSPENDED: { mot: "En attente de réponse", ton: "verrou", signe: "?" },
  DONE: { mot: "Terminée", ton: "succes", signe: "●" },
  FAILED: { mot: "En échec", ton: "erreur", signe: "▲" },
  ABANDONED: { mot: "Abandonnée", ton: "neutre", signe: "–" },
};

/** Le mot d'état d'une mission, en minuscule, pour l'inscrire dans une phrase
 *  ou une méta. **Le même état doit se lire pareil d'un écran à l'autre** : la
 *  liste de l'assistant, l'atelier de chiffrage et l'onglet Activité le
 *  nommaient de trois façons — « en course », « arbitrages attendus »,
 *  « En attente de réponse » — et le métreur croyait à trois situations. Ce
 *  mot est celui de l'étiquette `Etat`, décapitalisé. */
export const MOT_MISSION: Record<string, string> = {
  QUEUED: "en file",
  RUNNING: "en cours",
  SUSPENDED: "en attente de réponse",
  DONE: "terminée",
  FAILED: "en échec",
  ABANDONED: "abandonnée",
};

const TABLES = {
  affaire: ETATS_AFFAIRE,
  piece: ETATS_PIECE,
  travail: ETATS_TRAVAIL,
  instantane: ETATS_INSTANTANE,
  mission: ETATS_MISSION,
};

export function Etat({ genre, valeur }: { genre: keyof typeof TABLES; valeur: string }) {
  // Un état inconnu s'affiche **tel quel** plutôt que d'être masqué : une
  // montée de version qui ajoute un état doit se voir, non disparaître.
  const connu = TABLES[genre][valeur] ?? { mot: valeur, ton: "neutre" as Ton, signe: "·" };
  const classe = connu.ton === "neutre" ? "etiquette" : `etiquette etiquette--${connu.ton}`;
  return (
    <span className={classe}>
      <span aria-hidden="true">{connu.signe}</span>
      {connu.mot}
    </span>
  );
}
